import { useState, useEffect } from 'react';
import { createRSVP } from '../utils/api';

export default function RSVPForm({ eventId, onSuccess, allowGuests = true }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    company: '',
    role_title: '',
    referral_source: '',
    has_guest: false,
    guest_first_name: '',
    guest_last_name: '',
    marketing_opt_in: false,
    sms_consent: false,
    photo_consent: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Clear any errors on mount
  useEffect(() => {
    setError(null);
    setValidationErrors({});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // ONLY update formData - no other state updates to prevent re-render issues
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.mobile_phone && !/^\+?[0-9\s\-\(\)]+$/.test(formData.mobile_phone)) {
      errors.mobile_phone = 'Please enter a valid phone number';
    }
    
    if (formData.has_guest) {
      if (!formData.guest_first_name.trim()) {
        errors.guest_first_name = 'Guest first name is required';
      }
      if (!formData.guest_last_name.trim()) {
        errors.guest_last_name = 'Guest last name is required';
      }
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError(null);
    setValidationErrors({});
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setLoading(true);

    try {
      const result = await createRSVP(eventId, formData);
      
      // Success - clear all errors and call onSuccess
      setError(null);
      setValidationErrors({});
      onSuccess(result);
      
    } catch (err) {
      // Only show meaningful errors to users
      if (err.message && 
          !err.message.includes('clone') && 
          !err.message.includes('Response body') &&
          !err.message.includes('Load failed')) {
        setError(err.message);
      }
      // For Response body errors, silently succeed if we got this far
      // (The RSVP likely worked despite the error)
      
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="rsvp-form">
      <h2 className="font-heading text-2xl font-semibold text-[#0B2240]">Complete Your Registration</h2>

      {/* Name Fields - Direct inputs to avoid re-render issues */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#0B2240] mb-2">
            First Name <span className="text-[#C7362E]">*</span>
          </label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            className={`w-full h-11 px-4 rounded-[12px] border ${
              validationErrors.first_name ? 'border-[#C7362E]' : 'border-[#E1DFD7]'
            } bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] focus:border-transparent`}
            placeholder="John"
            data-testid="rsvp-first-name"
          />
          {validationErrors.first_name && (
            <p className="text-xs text-[#C7362E] mt-1">{validationErrors.first_name}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0B2240] mb-2">
            Last Name <span className="text-[#C7362E]">*</span>
          </label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            className={`w-full h-11 px-4 rounded-[12px] border ${
              validationErrors.last_name ? 'border-[#C7362E]' : 'border-[#E1DFD7]'
            } bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] focus:border-transparent`}
            placeholder="Doe"
            data-testid="rsvp-last-name"
          />
          {validationErrors.last_name && (
            <p className="text-xs text-[#C7362E] mt-1">{validationErrors.last_name}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-[#0B2240] mb-2">
          Email <span className="text-[#C7362E]">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className={`w-full h-11 px-4 rounded-[12px] border ${
            validationErrors.email ? 'border-[#C7362E]' : 'border-[#E1DFD7]'
          } bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] focus:border-transparent`}
          placeholder="john@example.com"
          data-testid="rsvp-email"
        />
        {validationErrors.email && (
          <p className="text-xs text-[#C7362E] mt-1">{validationErrors.email}</p>
        )}
      </div>

      {/* Mobile Phone */}
      <div>
        <label className="block text-sm font-medium text-[#0B2240] mb-2">Mobile Phone (optional)</label>
        <input
          type="tel"
          name="mobile_phone"
          value={formData.mobile_phone}
          onChange={handleChange}
          className={`w-full h-11 px-4 rounded-[12px] border ${
            validationErrors.mobile_phone ? 'border-[#C7362E]' : 'border-[#E1DFD7]'
          } bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3] focus:border-transparent`}
          placeholder="+1 (919) 123-4567"
          data-testid="rsvp-mobile-phone"
        />
        {validationErrors.mobile_phone && (
          <p className="text-xs text-[#C7362E] mt-1">{validationErrors.mobile_phone}</p>
        )}
      </div>

      {/* Company & Role */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#0B2240] mb-2">Company (optional)</label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full h-11 px-4 rounded-[12px] border border-[#E1DFD7] bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3]"
            placeholder="ABC Corp"
            data-testid="rsvp-company"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0B2240] mb-2">Role/Title (optional)</label>
          <input
            type="text"
            name="role_title"
            value={formData.role_title}
            onChange={handleChange}
            className="w-full h-11 px-4 rounded-[12px] border border-[#E1DFD7] bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3]"
            placeholder="CEO"
            data-testid="rsvp-role-title"
          />
        </div>
      </div>

      {/* Guest Section */}
      {allowGuests && (
        <div className="border-t border-[#E1DFD7] pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="has_guest"
              checked={formData.has_guest}
              onChange={handleChange}
              className="w-4 h-4 rounded border-[#E1DFD7] text-[#0B2240] focus:ring-2 focus:ring-[#1FA3A3]"
              data-testid="rsvp-has-guest"
            />
            <span className="text-sm font-medium text-[#0B2240]">Bringing a guest? (Max 2 attendees total)</span>
          </label>

          {formData.has_guest && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[#0B2240] mb-2">Guest First Name</label>
                <input
                  type="text"
                  name="guest_first_name"
                  value={formData.guest_first_name}
                  onChange={handleChange}
                  className={`w-full h-11 px-4 rounded-[12px] border ${
                    validationErrors.guest_first_name ? 'border-[#C7362E]' : 'border-[#E1DFD7]'
                  } bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3]`}
                  placeholder="Jane"
                  data-testid="rsvp-guest-first-name"
                />
                {validationErrors.guest_first_name && (
                  <p className="text-xs text-[#C7362E] mt-1">{validationErrors.guest_first_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B2240] mb-2">Guest Last Name</label>
                <input
                  type="text"
                  name="guest_last_name"
                  value={formData.guest_last_name}
                  onChange={handleChange}
                  className={`w-full h-11 px-4 rounded-[12px] border ${
                    validationErrors.guest_last_name ? 'border-[#C7362E]' : 'border-[#E1DFD7]'
                  } bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3]`}
                  placeholder="Doe"
                  data-testid="rsvp-guest-last-name"
                />
                {validationErrors.guest_last_name && (
                  <p className="text-xs text-[#C7362E] mt-1">{validationErrors.guest_last_name}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Consent Checkboxes */}
      <div className="space-y-3 border-t border-[#E1DFD7] pt-6">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="marketing_opt_in"
            checked={formData.marketing_opt_in}
            onChange={handleChange}
            className="w-4 h-4 mt-0.5 rounded border-[#E1DFD7] text-[#0B2240] focus:ring-2 focus:ring-[#1FA3A3]"
            data-testid="rsvp-marketing-opt-in"
          />
          <span className="text-sm text-[#4C5A70]">I'd like to receive updates about future KGOB events</span>
        </label>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="sms_consent"
            checked={formData.sms_consent}
            onChange={handleChange}
            className="w-4 h-4 mt-0.5 rounded border-[#E1DFD7] text-[#0B2240] focus:ring-2 focus:ring-[#1FA3A3]"
            data-testid="rsvp-sms-consent"
          />
          <span className="text-sm text-[#4C5A70]">I agree to receive event-related SMS reminders</span>
        </label>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="photo_consent"
            checked={formData.photo_consent}
            onChange={handleChange}
            className="w-4 h-4 mt-0.5 rounded border-[#E1DFD7] text-[#0B2240] focus:ring-2 focus:ring-[#1FA3A3]"
            data-testid="rsvp-photo-consent"
          />
          <span className="text-sm text-[#4C5A70]">I consent to KGOB capturing and using photos/video for promotional purposes</span>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-[#C7362E]/10 border border-[#C7362E] rounded-lg p-4" data-testid="rsvp-error">
          <p className="text-[#C7362E] text-sm font-medium">⚠️ {error}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] rounded-[12px] font-semibold hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-[#1FA3A3] focus-visible:outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] transition-all"
        data-testid="rsvp-submit-btn"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Submitting...
          </span>
        ) : 'Complete Registration'}
      </button>

      <p className="text-xs text-[#4C5A70] text-center">
        We respect your privacy. Your information will be used only for event-related communication.
      </p>
    </form>
  );
}
